import * as React from "react"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormControl from "@mui/material/FormControl"
import FormLabel from "@mui/material/FormLabel"
import theme from "src/v2/theme/theme"
import {Typography, Grid} from "@mui/material"
import PropTypes from "prop-types"

const radioStyles = {
    "&.Mui-checked": {
        color: theme.palette.primary.main2,
        "& .MuiSvgIcon-root:last-of-type": {
            transform: "scale(0.5)",
        },
    },
    "& .MuiSvgIcon-root": {
        width: "20px",
        height: "20px",
    },
    "& .MuiSvgIcon-root:first-of-type": {
        color: theme.palette.white.main,
    },
}

const GridRadioButton = ({
    title,
    radioButtonItems,
    defaultRadioValue = "",
    value,
    onChange,
    columns,
    direction,
    itemsPerColumn,
    spacing,
    horizontalSpacing,
}) => {
    // Divide radioButtonItems into chunks according to itemsPerColumn
    const chunks = []
    for (let i = 0; i < radioButtonItems.length; i += itemsPerColumn) {
        chunks.push(radioButtonItems.slice(i, i + itemsPerColumn))
    }

    const spacingConfig = (() => {
        const defaultSpacing = {mt: 0, mb: 0, ml: 0, mr: 0, pt: 0, pb: 0, pl: 0, pr: 0}
        if (spacing === undefined || spacing === null) return defaultSpacing
        if (typeof spacing === "number") {
            return {...defaultSpacing, mt: spacing, mb: spacing}
        }
        return {
            mt: spacing.mt ?? defaultSpacing.mt,
            mb: spacing.mb ?? defaultSpacing.mb,
            ml: spacing.ml ?? defaultSpacing.ml,
            mr: spacing.mr ?? defaultSpacing.mr,
            pt: spacing.pt ?? defaultSpacing.pt,
            pb: spacing.pb ?? defaultSpacing.pb,
            pl: spacing.pl ?? defaultSpacing.pl,
            pr: spacing.pr ?? defaultSpacing.pr,
        }
    })()

    const resolvedHorizontalSpacing = React.useMemo(() => {
        if (horizontalSpacing === undefined || horizontalSpacing === null) return 2
        if (typeof horizontalSpacing === "number") return horizontalSpacing
        if (typeof horizontalSpacing === "object") return horizontalSpacing
        return 2
    }, [horizontalSpacing])

    return (
        <FormControl
            sx={{
                display: "flex",
                width: "100%",
                mt: spacingConfig.mt,
                mb: spacingConfig.mb,
                ml: spacingConfig.ml,
                mr: spacingConfig.mr,
                pt: spacingConfig.pt,
                pb: spacingConfig.pb,
                pl: spacingConfig.pl,
                pr: spacingConfig.pr,
            }}
        >
            <FormLabel id="access-level-radio-buttons-group-label">
                <Typography
                    variant="contraHeading"
                    sx={{color: theme.palette.primary.white70, fontSize: "70%"}}
                >
                    {title}
                </Typography>
            </FormLabel>
            <RadioGroup
                aria-labelledby="access-level-radio-buttons-group-label"
                defaultValue={defaultRadioValue}
                value={value ?? defaultRadioValue}
                name="radio-buttons-group"
                onChange={onChange}
                sx={{mt: 1}}
            >
                <Grid container spacing={2} direction={"row"}>
                    {chunks.map((chunk, index) => {
                        const colSpan = Math.max(1, Math.min(12, Math.floor(12 / columns)))
                        return (
                            <Grid item xs={12} md={colSpan} key={index}>
                                <Grid
                                    container
                                    direction={direction}
                                    rowSpacing={2}
                                    columnSpacing={resolvedHorizontalSpacing}
                                >
                                    {chunk.map((option) => (
                                        <Grid item key={option.value} xs={12} md={colSpan}>
                                            <FormControlLabel
                                                value={option.value}
                                                control={<Radio sx={radioStyles} />}
                                                label={
                                                    <Typography
                                                        variant="body2"
                                                        color="common.white"
                                                        sx={{letterSpacing: 0.3, fontWeight: 300, fontSize: "80%"}}
                                                    >
                                                        {option.label}
                                                    </Typography>
                                                }
                                                sx={{height: "24px"}}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        )
                    })}
                </Grid>
            </RadioGroup>
        </FormControl>
    )
}

GridRadioButton.propTypes = {
    title: PropTypes.string.isRequired,
    defaultRadioValue: PropTypes.string,
    value: PropTypes.string,
    radioButtonItems: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
    direction: PropTypes.string.isRequired,
    onChange: PropTypes.func,
    columns: PropTypes.number.isRequired,
    itemsPerColumn: PropTypes.number.isRequired,
    spacing: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({
            mt: PropTypes.number,
            mb: PropTypes.number,
            ml: PropTypes.number,
            mr: PropTypes.number,
            pt: PropTypes.number,
            pb: PropTypes.number,
            pl: PropTypes.number,
            pr: PropTypes.number,
        }),
    ]),
    horizontalSpacing: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.objectOf(PropTypes.number),
    ]),
}

export default GridRadioButton
